import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQueryClient, QueryFunctionContext } from '@tanstack/react-query';
import type { IFigureDto } from '../models/IFigureDto';
import type { IPaginatedResponse } from '../models/IPaginatedResponse';
import FigurService from '../services/figureService';
import type { IFigureCollection } from '../models/IFigureCollection';
import { useAppEvent } from './useAppEvent';
import { applyFigureRemovedToInfiniteData, applyFigureUpdatedToInfiniteData } from '../query/figureCacheUpdates';

type QueryData = {
  pages: Array<{
    data: IFigureDto[];
    hasMore: boolean;
    nextCursor: string | null;
    totalCount: number;
  }>;
  pageParams: number[];
};

export interface UseGridFiguresSearchState {
  results: IFigureDto[];
  isSearchMode: boolean;
  totalCount: number;
  collectionId: number | null;
}

interface UseGridFiguresParams {
  collectionId: number;
  seriesId?: number | null;
  selectedTags?: string[];
  sharedSearchState?: UseGridFiguresSearchState | null;
  favoritterCollectionId?: number;
  defaultCollectionId?: number;
}

export interface UseGridFiguresResult {
  figures: IFigureDto[];
  allFigures: IFigureDto[];
  isLoading: boolean;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  totalLoaded: number;
  totalAvailable: number;
  currentCollection?: IFigureCollection;
}

export function useGridFigures({
  collectionId,
  seriesId = null,
  selectedTags = [],
  sharedSearchState = null,
  favoritterCollectionId,
}: UseGridFiguresParams): UseGridFiguresResult {
  const queryClient = useQueryClient();
  const [totalLoaded, setTotalLoaded] = useState<number>(0);
  const [totalAvailable, setTotalAvailable] = useState<number>(0);

  const effectiveSearchResults =
    sharedSearchState?.collectionId === collectionId ? sharedSearchState.results : [];
  const effectiveIsSearchMode =
    sharedSearchState?.collectionId === collectionId ? sharedSearchState.isSearchMode : false;
  const effectiveSearchTotalCount =
    sharedSearchState?.collectionId === collectionId ? sharedSearchState.totalCount : 0;

  const limit = 50;

  const collectionQuery = useInfiniteQuery<IPaginatedResponse<IFigureDto>, Error, QueryData>({
    queryKey: ['figures-grid', collectionId, seriesId ?? 0],
    queryFn: async (context: QueryFunctionContext) => {
      const pageParam = Number(context.pageParam ?? 1);
      const favoriteId = Number(favoritterCollectionId ?? 0);

      const result =
        collectionId === favoriteId
          ? await FigurService.getFavoriteFigures(pageParam, limit, seriesId ?? 0)
          : await FigurService.getFigures(collectionId, pageParam, limit, seriesId ?? 0);

      return result;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore && lastPage.nextCursor ? parseInt(lastPage.nextCursor) : undefined;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !effectiveIsSearchMode,
  });

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = collectionQuery;

  const figureHasTags = useCallback((figure: IFigureDto, tags: string[]): boolean => {
    if (tags.length === 0) return true;

    const bfsTags: string[] = (figure.metadata || '')
      .split('\n')
      .filter(Boolean)
      .map((tag) => tag.trim().toLowerCase());

    const customerTags: string[] = (figure.customerMetadata || '')
      .split('#')
      .filter(Boolean)
      .map((tagStr) => {
        const parts = tagStr.trim().split(':');
        return parts[0].trim().toLowerCase();
      });

    const allFigureTags = [...bfsTags, ...customerTags];

    return tags.some((selectedTag) => allFigureTags.includes(selectedTag.toLowerCase()));
  }, []);

  const allFigures = useMemo(
    () => (effectiveIsSearchMode ? effectiveSearchResults : data?.pages.flatMap((page) => page.data) || []),
    [effectiveIsSearchMode, effectiveSearchResults, data],
  );

  const figures = useMemo(() => {
    if (selectedTags.length === 0) return allFigures;
    return allFigures.filter((figure) => figureHasTags(figure, selectedTags));
  }, [allFigures, selectedTags, figureHasTags]);

  useAppEvent('figureRemoved', ({ removedFigureId, collectionId: removedFrom }) => {
    queryClient.setQueryData<QueryData>(['figures-grid', collectionId, seriesId ?? 0], (oldData) => {
      return applyFigureRemovedToInfiniteData(oldData, {
        removedFigureId,
        removedFromCollectionId: removedFrom,
        currentCollectionId: collectionId,
      });
    });
  });

  useAppEvent('figureUpdated', (updatedFigure) => {
    queryClient.setQueryData<QueryData>(['figures-grid', collectionId, seriesId ?? 0], (oldData) => {
      return applyFigureUpdatedToInfiniteData(oldData, updatedFigure);
    });
  });

  useEffect(() => {
    if (collectionId === Number(favoritterCollectionId ?? 0)) {
      const ids = figures.map((f) => f.imageId);
      const unique = new Set(ids);
      const dupIds = ids.filter((id, i) => ids.indexOf(id) !== i);
      // eslint-disable-next-line no-console
      console.debug('Favorites view data', {
        total: ids.length,
        unique: unique.size,
        dupes: Array.from(new Set(dupIds)),
        pageSizes: data?.pages?.map((p) => p.data.length) ?? [],
      });
    }
  }, [collectionId, figures, data?.pages, favoritterCollectionId]);

  useEffect(() => {
    if (effectiveIsSearchMode) {
      setTotalLoaded(effectiveSearchResults.length);
      setTotalAvailable(effectiveSearchTotalCount);
    } else if (figures.length > 0) {
      setTotalLoaded(figures.length);
      if (data?.pages[0].totalCount) {
        setTotalAvailable(data.pages[0].totalCount);
      }
    }
  }, [effectiveIsSearchMode, effectiveSearchResults.length, effectiveSearchTotalCount, figures.length, data?.pages]);

  return {
    figures,
    allFigures,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    totalLoaded,
    totalAvailable,
    fetchNextPage,
  };
}

